<template>
  <div class="scanner-container">
    <div v-show="!isLoading">
      <video poster="data:image/gif,AAAA" ref="scanner"></video>
      <div class="overlay-element"></div>
      <div class="laser"></div>
    </div>
  </div>
</template>

<script>
import {
  BrowserMultiFormatReader,
  DecodeHintType,
  BarcodeFormat,
  Exception,
} from "@zxing/library";

export default {
  name: "stream-barcode-reader",

  props: {
    stopCamera: {
      type: Boolean,
      default: false,
    },
    // Restringe los formatos a decodificar (mejora velocidad/precisión).
    // Ej: ['EAN_13','EAN_8','UPC_A','CODE_128']. Si es null, multi-formato.
    formats: {
      type: Array,
      default: null,
    },
  },

  data() {
    return {
      isLoading: true,
      codeReader: null,
      isMediaStreamAPISupported:
        navigator &&
        navigator.mediaDevices &&
        "enumerateDevices" in navigator.mediaDevices,
    };
  },

  mounted() {
    if (!this.isMediaStreamAPISupported) {
      throw new Exception("Media Stream API is not supported");
    }
    // 200ms entre escaneos para no saturar el decodificador
    this.codeReader = new BrowserMultiFormatReader(this.buildHints(), 200);
    this.start();
    this.$refs.scanner.oncanplay = () => {
      this.isLoading = false;
      this.$emit("loaded");
    };
  },

  beforeDestroy() {
    this.safeReset();
  },

  methods: {
    // Pistas de decodificación: try-harder + formatos opcionales
    buildHints() {
      const hints = new Map();
      hints.set(DecodeHintType.TRY_HARDER, true);
      if (this.formats && this.formats.length) {
        const fmts = this.formats
          .map((f) => BarcodeFormat[f])
          .filter((f) => f !== undefined);
        if (fmts.length) hints.set(DecodeHintType.POSSIBLE_FORMATS, fmts);
      }
      return hints;
    },

    start() {
      const onResult = (result) => {
        if (result) this.$emit("decode", result.text);
      };
      // Preferir cámara trasera y mayor resolución para leer códigos pequeños
      const constraints = {
        audio: false,
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      };
      try {
        if (typeof this.codeReader.decodeFromConstraints === "function") {
          this.codeReader
            .decodeFromConstraints(constraints, this.$refs.scanner, onResult)
            .catch(() => this.startFallback(onResult));
        } else {
          this.startFallback(onResult);
        }
      } catch (e) {
        this.startFallback(onResult);
      }
    },

    // Respaldo: elegir la cámara trasera por etiqueta o usar la última disponible
    startFallback(onResult) {
      this.codeReader
        .listVideoInputDevices()
        .then((devices) => {
          let deviceId;
          const back = devices.find((d) =>
            /back|rear|environment|tras/i.test(d.label)
          );
          if (back) deviceId = back.deviceId;
          else if (devices.length) deviceId = devices[devices.length - 1].deviceId;
          this.codeReader.decodeFromVideoDevice(deviceId, this.$refs.scanner, onResult);
        })
        .catch(() => {
          this.codeReader.decodeFromVideoDevice(undefined, this.$refs.scanner, onResult);
        });
    },

    safeReset() {
      try {
        if (this.codeReader) this.codeReader.reset();
      } catch (e) {
        /* noop */
      }
    },
  },

  watch: {
    stopCamera(e) {
      if (e) {
        this.safeReset();
      } else {
        this.start();
      }
    },
  },
};
</script>

<style scoped>
video {
  max-width: 100%;
  max-height: 100%;
}
.scanner-container {
  position: relative;
}

.overlay-element {
  position: absolute;
  top: 0;
  width: 100%;
  height: 99%;
  background: rgba(30, 30, 30, 0.1);

  -webkit-clip-path: polygon(
    0% 0%,
    0% 100%,
    20% 100%,
    20% 20%,
    80% 20%,
    80% 80%,
    20% 80%,
    20% 100%,
    100% 100%,
    100% 0%
  );
  clip-path: polygon(
    0% 0%,
    0% 100%,
    20% 100%,
    20% 20%,
    80% 20%,
    80% 80%,
    20% 80%,
    20% 100%,
    100% 100%,
    100% 0%
  );
}

.laser {
  width: 80%;
  margin-left: 10%;
  background-color: tomato;
  height: 1px;
  position: absolute;
  top: 20%;
  z-index: 2;
  box-shadow: 0 0 4px red;
  -webkit-animation: scanning 2s infinite;
  animation: scanning 4s infinite;
}
@-webkit-keyframes scanning {
  50% {
    -webkit-transform: translateY(200px);
    transform: translateY(200px);
  }
}
@keyframes scanning {
  50% {
    -webkit-transform: translateY(200px);
    transform: translateY(200px);
  }
}
</style>
